import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { ZendraLocalizationPipe } from '../pipes/zendra-localization.pipe';
import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';
import { localizeWithFallback } from '../utils/localization-utils';
import { ZnSelectQueryParams } from '../generic-form/interfaces/IGenericFieldOptions';
import { ZnSelectLoader, ZnSelectOptionsLoader } from './zn-select-loader';
import type { ZnSelectOption } from './zn-select.types';
import {
  areZnSelectOptionsAllSelected,
  buildZnSelectLoadOptionsErrorMessage,
  createZnSelectOverlayConfig,
  fetchAllZnSelectOptions,
  normalizeZnSelectIds,
} from './zn-select-helpers';
import { ZnButton } from '../zn-button/zn-button';

@Component({
  selector: 'zn-multi-select',
  standalone: true,
  imports: [CommonModule, FormsModule, ZendraLocalizationPipe, OverlayModule, ZnButton],
  templateUrl: './zn-multi-select.html',
  styleUrls: ['./zn-select.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ZnMultiSelect),
      multi: true,
    },
  ],
})
export class ZnMultiSelect implements ControlValueAccessor, OnDestroy {
  private static masterCheckSequence = 0;

  readonly masterCheckId = `zn-master-check-${ZnMultiSelect.masterCheckSequence++}`;

  options = input<Array<string | { key: string; value: string }>>([]);
  optionsLoader = input<ZnSelectOptionsLoader | undefined>(undefined);
  showSearch = input<boolean>(true);
  readonly filterInputId = `${this.masterCheckId}-multi-filter`;

  placeholder = input<string>('');
  showPlaceholder = input<boolean>(true);
  showClearSelection = input<boolean>(true);
  errorEntityKey = input<string>('');
  resolveIds = input<boolean>(true);
  deferSelection = input<boolean>(true);
  emptyDisplayValue = input<string>('');
  /**
   * Makes an empty model value represent the global "Select all" state.
   * This is opt-in because most consumers distinguish an empty selection
   * from selecting every option.
   */
  emptySelectionIsAll = input<boolean>(false);
  localizationResource = input<ZendraLocalizationServices>(
    ZendraLocalizationServices.LanguageService
  );

  @Input() openUp = false;

  selectedChange = output<string | string[] | null>();

  @ViewChild('dropdownTemplate') dropdownTemplate!: TemplateRef<any>;
  @ViewChild('selectTrigger', { read: ElementRef }) selectTrigger?: ElementRef<HTMLElement>;

  readonly dropdownOpen = signal(false);
  readonly search = signal('');
  readonly selected = signal<string[]>([]);
  readonly draftSelected = signal<string[]>([]);
  readonly activeIndex = signal<number>(-1);
  readonly loading = signal<boolean>(false);
  readonly loadingMore = signal<boolean>(false);
  readonly allSelected = signal<boolean>(false);
  readonly allSelectedSearchTerm = signal<string | null>(null);

  private readonly asyncOptions = signal<ZnSelectOption[]>([]);
  private readonly staticOptions = signal<ZnSelectOption[]>([]);
  private readonly currentValue = signal<string[] | string | null>(null);
  private readonly loaderTick = signal(0);
  private readonly totalCount = signal<number | null>(null);
  private readonly lastBatchSize = signal<number>(0);
  private readonly keyPressed = signal(false);

  readonly displayOptions = computed(() => {
    return this.optionsLoader() ? this.asyncOptions() : this.staticOptions();
  });

  readonly filteredOptions = computed(() => {
    const options = this.displayOptions();
    if (this.optionsLoader()) {
      return options;
    }

    const term = this.search().trim().toLowerCase();
    if (!this.showSearch() || !term) {
      return options;
    }

    return options.filter(option => option.value.toLowerCase().includes(term));
  });

  readonly hasFilterSelectionValue = computed(
    () => this.draftSelected().length > 0 || this.allSelected()
  );

  readonly filterSelectionLabelValue = computed(() => {
    const selectedCount = this.draftSelected().length;

    if (this.allSelected() && !this.allSelectedSearchTerm()) {
      return localizeWithFallback(
        this.localizationService,
        `${this.localizationResource()}::ZnAll`
      );
    }

    if (!selectedCount) {
      return '';
    }

    if (selectedCount <= 3) {
      const labels = this.draftSelected()
        .slice(0, 3)
        .map(id => this.getLabelForId(id))
        .filter(Boolean);
      if (labels.length === selectedCount) {
        return labels.join(', ');
      }
    }

    const template = localizeWithFallback(
      this.localizationService,
      `${this.localizationResource()}::ZnSelectSelectedCount`
    );
    if (template && template.includes('{0}')) {
      return template.replace('{0}', String(selectedCount));
    }

    return `${selectedCount}`;
  });

  readonly filterInputLabel = computed(() => {
    const placeholder = this.placeholder() || 'FilterPlaceholder';
    const key = placeholder.includes('::') ? placeholder : `${this.localizationResource()}::${placeholder}`;
    return localizeWithFallback(this.localizationService, key);
  });

  readonly filterInputPlaceholderValue = computed(() => {
    if (!this.hasFilterSelectionValue() && this.emptyDisplayValue()) {
      return '';
    }

    if (!this.showPlaceholder()) {
      return '';
    }

    const customPlaceholder = this.placeholder();
    if (customPlaceholder) {
      const resolvedKey = customPlaceholder.includes('::')
        ? customPlaceholder
        : `${this.localizationResource()}::${customPlaceholder}`;
      return localizeWithFallback(this.localizationService, resolvedKey);
    }

    return localizeWithFallback(
      this.localizationService,
      `${this.localizationResource()}::FilterPlaceholder`
    );
  });
  readonly filterInputValue = computed(() => {
    if (this.hasFilterSelectionValue()) {
      return this.filterSelectionLabelValue();
    }

    const emptyValue = this.emptyDisplayValue();
    if (!emptyValue) {
      return '';
    }

    const resolvedKey = emptyValue.includes('::')
      ? emptyValue
      : `${this.localizationResource()}::${emptyValue}`;
    return localizeWithFallback(this.localizationService, resolvedKey);
  });

  readonly hasMore = computed(() => this.loader.hasMore());

  disabled = false;

  private readonly localizationService = inject(LocalizationService);
  private readonly toaster = inject(ToasterService);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  private readonly optionsMap = new Map<string, string>();
  private readonly selectedValueMap = new Map<string, string>();
  private readonly pendingResolveIds = new Set<string>();

  private readonly pageSize = 20;
  private skipCount = 0;
  private overlayRef: OverlayRef | null = null;

  private onChange: (value: string | string[] | null) => void = () => {};
  private onTouched: () => void = () => {};
  private labelLoadSubscriptions: Subscription[] = [];
  private selectAllSubscription?: Subscription;
  private selectAllRequestId = 0;
  private pendingSearchSelection: string[] | null = null;

  private readonly loader: ZnSelectLoader;

  constructor() {
    this.loader = new ZnSelectLoader({
      getLoader: () => this.optionsLoader(),
      getPageSize: () => this.pageSize,
      getSkipCount: () => this.skipCount,
      setSkipCount: value => {
        this.skipCount = value;
      },
      getSearch: () => this.search(),
      isDropdownOpen: () => this.dropdownOpen(),
      getOptionsContainer: () => this.getOptionsContainerElement(),
      setLoading: value => this.loading.set(value),
      setLoadingMore: value => this.loadingMore.set(value),
      onOptionsLoaded: (payload, total, append, previousScrollTop) => {
        if (!append) {
          this.optionsMap.clear();
          this.asyncOptions.set([]);
        }

        payload.forEach(option => {
          const key = String(option.key);
          const value = String(option.value);
          this.optionsMap.set(key, value);
          this.selectedValueMap.set(key, value);
        });

        const nextOptions: ZnSelectOption[] = payload.map(option => ({
          key: String(option.key),
          value: String(option.value),
        }));

        if (append) {
          this.asyncOptions.update(current => [...current, ...nextOptions]);
        } else {
          this.asyncOptions.set(nextOptions);
        }

        if (total !== null) {
          this.totalCount.set(total);
        }

        this.onOptionsLoaded(append);
        this.loaderTick.update(value => value + 1);

        const optionsContainer = this.getOptionsContainerElement();
        if (append && optionsContainer) {
          requestAnimationFrame(() => {
            optionsContainer.scrollTo({ top: previousScrollTop });
          });
        }
      },
      onLoadError: error => {
        this.toaster.error(this.buildLoadOptionsErrorMessage(error), null, { life: 5000 });
      },
      getAsyncOptionsLength: () => this.asyncOptions().length,
      getTotalCount: () => this.totalCount(),
      setTotalCount: value => this.totalCount.set(value),
      setLastBatchSize: value => this.lastBatchSize.set(value),
      getLastBatchSize: () => this.lastBatchSize(),
      onResetOptions: () => {
        this.asyncOptions.set([]);
        this.optionsMap.clear();
      },
    });

    effect(() => {
      const rawOptions = this.options();
      const hasLoader = !!this.optionsLoader();

      if (!hasLoader) {
        this.optionsMap.clear();
        this.selectedValueMap.clear();

        this.staticOptions.set(
          rawOptions.map(option => {
            if (typeof option === 'string') {
              return { key: option, value: option };
            }

            return {
              key: String(option.key),
              value: String(option.value),
            };
          })
        );
      }

      rawOptions.forEach(option => {
        if (typeof option === 'string') {
          this.optionsMap.set(option, option);
          this.selectedValueMap.set(option, option);
          return;
        }

        const key = String(option.key);
        const value = String(option.value);
        this.optionsMap.set(key, value);
        this.selectedValueMap.set(key, value);
      });

      const current = this.currentValue();
      if ((current !== null && current !== undefined) || this.emptySelectionIsAll()) {
        this.syncFromValue(current);
      }
    });

    effect(() => {
      const loader = this.optionsLoader();
      this.loaderTick();
      const value = this.currentValue();
      if (loader && value && this.selectedValueMap.size === 0) {
        this.resolveSelectedLabels(value);
      }
    });

    effect(() => {
      if (!this.dropdownOpen()) {
        return;
      }

      const options = this.filteredOptions();
      if (options.length === 0) {
        this.activeIndex.set(-1);
        return;
      }

      if (this.activeIndex() < 0 || this.activeIndex() >= options.length) {
        this.activeIndex.set(0);
      }
    });
  }

  writeValue(value: string[] | string | null): void {
    this.currentValue.set(value);
    this.syncFromValue(value);

    const normalized = normalizeZnSelectIds(value);
    const hasMissingLabels = normalized.some(id => !this.selectedValueMap.has(id));
    if (hasMissingLabels) {
      this.resolveSelectedLabels(value);
    }
  }

  registerOnChange(fn: (value: string | string[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleDropdown(): void {
    if (this.disabled) {
      return;
    }

    if (this.dropdownOpen()) {
      this.closeDropdown();
      return;
    }

    this.openDropdown();
  }

  open(): void {
    if (this.disabled || this.dropdownOpen()) {
      return;
    }

    this.openDropdown();
    this.focusTrigger();
  }

  isOptionSelected(id: string | number): boolean {
    return this.draftSelected().includes(String(id));
  }

  toggleOption(id: string | number): void {
    if (this.disabled) {
      return;
    }

    const idStr = String(id);

    if (this.allSelected()) {
      this.allSelected.set(false);
      this.allSelectedSearchTerm.set(null);
      this.draftSelected.set([...this.displayOptions().map(option => String(option.key))]);
    }

    const selected = this.draftSelected();
    const nextValue = selected.includes(idStr)
      ? selected.filter(optionId => optionId !== idStr)
      : [...selected, idStr];

    this.draftSelected.set(nextValue);
    this.syncSelectAllFlagFromDraftSelection(nextValue);

    if (!this.deferSelection()) {
      this.commitDraftSelection();
    }
  }

  toggleSelectAll(): void {
    if (this.disabled) {
      return;
    }

    const searchTerm = this.search().trim();
    if (!this.isSelectAllActive() && this.optionsLoader() && searchTerm) {
      this.allSelected.set(true);
      this.allSelectedSearchTerm.set(searchTerm);
      this.draftSelected.set([...this.displayOptions().map(option => String(option.key))]);
      this.selectAllForSearch(searchTerm);
      if (!this.deferSelection()) {
        this.commitDraftSelection();
      }
      return;
    }

    if (this.isSelectAllActive()) {
      this.draftSelected.set([]);
      this.allSelected.set(false);
      this.allSelectedSearchTerm.set(null);
      this.pendingSearchSelection = null;
      if (!this.deferSelection()) {
        this.commitDraftSelection();
      }
      return;
    }

    this.draftSelected.set([...this.displayOptions().map(option => String(option.key))]);
    this.allSelected.set(true);
    this.allSelectedSearchTerm.set(null);

    if (!this.deferSelection()) {
      this.commitDraftSelection();
    }
  }

  clearSelection(): void {
    if (this.disabled) {
      return;
    }

    this.draftSelected.set([]);
    this.allSelected.set(false);
    this.allSelectedSearchTerm.set(null);
    this.pendingSearchSelection = null;

    if (!this.deferSelection()) {
      this.commitDraftSelection();
    }
  }

  reset(): void {
    this.search.set('');
    this.selected.set([]);
    this.draftSelected.set([]);
    this.currentValue.set(null);
    this.allSelected.set(false);
    this.allSelectedSearchTerm.set(null);
    this.pendingSearchSelection = null;
    this.activeIndex.set(-1);
    this.keyPressed.set(false);

    if (this.dropdownOpen()) {
      this.closeDropdown();
    }
  }

  applyDraftSelection(): void {
    if (this.disabled) {
      return;
    }

    this.commitDraftSelection();
    this.closeDropdown();
  }

  refreshOptions(): void {
    if (this.disabled || this.loading() || !this.optionsLoader()) {
      return;
    }

    this.loader.resetPagination();
    this.loadOptions({ forceRefresh: true, search: this.search() });
  }

  handleKeydown(event: KeyboardEvent): void {
    this.keyPressed.set(true);
    const key = event.key;

    if (key === 'Tab') {
      this.closeDropdown();
    }

    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      if (this.dropdownOpen()) {
        this.closeDropdown();
      }
      return;
    }

    if (!this.dropdownOpen()) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        this.openDropdown();
        this.focusTrigger();
      }
      return;
    }

    const options = this.filteredOptions();

    if (key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (options.length === 0) {
        return;
      }

      const current = this.activeIndex();
      const next = current < 0 ? 0 : Math.min(current + 1, options.length - 1);
      this.activeIndex.set(next);
      this.scrollActiveOptionIntoView(true);
      return;
    }

    if (key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      if (options.length === 0) {
        return;
      }

      const current = this.activeIndex();
      const next = current < 0 ? options.length - 1 : Math.max(current - 1, 0);
      this.activeIndex.set(next);
      this.scrollActiveOptionIntoView(true);
      return;
    }

    if (key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      const target = event.target as HTMLElement | null;
      if (this.deferSelection() && !target?.closest('.option-item')) {
        this.applyDraftSelection();
        return;
      }

      const index = this.activeIndex();
      if (index >= 0 && index < options.length) {
        this.toggleOption(options[index].key);
      }
      return;
    }

    if (key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeDropdown();
    }
  }

  onSearchChange(value: string): void {
    this.search.set(value);
    this.onSearchSelectionChange(value);

    if (!this.optionsLoader()) {
      return;
    }

    this.loader.queueSearch(value);
  }

  onOptionsScroll(event: Event): void {
    if (this.loading() || this.loadingMore() || !this.optionsLoader()) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const threshold = 24;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
    if (!nearBottom || !this.hasMore()) {
      return;
    }

    this.loader.loadNextPage();
  }

  handleSelectAllContainerClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, label')) {
      return;
    }
    this.toggleSelectAll();
  }

  isSelectAllCheckboxChecked(): boolean {
    const totalOptions = this.displayOptions().length;
    if (totalOptions === 0) {
      return false;
    }
    return this.isSelectAllActive();
  }

  isSelectAllCheckboxIndeterminate(): boolean {
    const totalOptions = this.displayOptions().length;
    if (totalOptions === 0 || this.isSelectAllCheckboxChecked()) {
      return false;
    }
    return this.draftSelected().length > 0;
  }

  isActiveIndex(index: number): boolean {
    if(this.keyPressed()) {
      return index === this.activeIndex();
    }
  }

  getLabelForId(id: string): string {
    return this.selectedValueMap.get(id) ?? this.optionsMap.get(id) ?? id;
  }

  ngOnDestroy(): void {
    this.loader.destroy();
    this.selectAllSubscription?.unsubscribe();
    this.labelLoadSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.labelLoadSubscriptions = [];

    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private syncFromValue(value: string[] | string | null): void {
    const normalized = normalizeZnSelectIds(value);
    this.selected.set(normalized);
    this.draftSelected.set([...normalized]);
    this.pendingSearchSelection = null;

    const isEmptyValueAsAll = this.emptySelectionIsAll() && normalized.length === 0;
    if (isEmptyValueAsAll || normalized.length > 0) {
      this.allSelected.set(isEmptyValueAsAll);
      this.allSelectedSearchTerm.set(null);
    }
  }

  private emitSelection(values: string[]): void {
    this.onChange(values);
    this.selectedChange.emit(values);
  }

  private emitRawValue(value: string | string[] | null): void {
    this.onChange(value);
    this.selectedChange.emit(value);
  }

  private openDropdown(): void {
    this.dropdownOpen.set(true);
    this.onDropdownOpened();
    this.activeIndex.set(-1);

    if (this.optionsLoader()) {
      this.loader.resetPagination();

      const params: ZnSelectQueryParams = {
        search: this.getLoaderSearchTermOnOpen(this.search()),
      };

      const selectedIds = this.getSelectionForLoader();
      if (selectedIds.length > 0) {
        params.ids = selectedIds;
      }
      this.loadOptions(params);
    }

    this.scrollActiveOptionIntoView(false);

    if (!this.selectTrigger?.nativeElement) {
      return;
    }

    const config = createZnSelectOverlayConfig(this.overlay, this.selectTrigger.nativeElement, this.openUp, 320);
    this.overlayRef = this.overlay.create(config);

    const portal = new TemplatePortal(this.dropdownTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);

    this.overlayRef.outsidePointerEvents().subscribe(event => {
      const target = event.target as HTMLElement;
      const clickedInsideTrigger = this.selectTrigger?.nativeElement.contains(target);

      if (!clickedInsideTrigger) {
        this.closeDropdown();
      }
    });

    this.focusSearchInput();
  }

  private closeDropdown(): void {
    this.dropdownOpen.set(false);
    this.onTouched();
    this.activeIndex.set(-1);
    this.onDropdownClosed();

    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.keyPressed.set(false);
  }

  private onDropdownOpened(): void {
    if (this.allSelected() && !this.allSelectedSearchTerm()) {
      this.draftSelected.set([...this.displayOptions().map(option => String(option.key))]);
      return;
    }

    this.draftSelected.set([...this.selected()]);
  }

  private onDropdownClosed(): void {
    this.draftSelected.set([...this.selected()]);
  }

  private onSearchSelectionChange(value: string): void {
    const term = value.trim();

    if (term) {
      if (this.allSelected() && !this.allSelectedSearchTerm()) {
        this.allSelectedSearchTerm.set(term);
      } else if (this.allSelectedSearchTerm() && this.allSelectedSearchTerm() !== term) {
        this.allSelectedSearchTerm.set(term);
      }
    } else if (this.allSelectedSearchTerm()) {
      this.allSelectedSearchTerm.set(null);
    }

    this.pendingSearchSelection = null;
  }

  private onOptionsLoaded(append: boolean): void {
    const activeSearch = this.search().trim();

    if (
      this.allSelected() &&
      (!this.allSelectedSearchTerm() || this.allSelectedSearchTerm() === activeSearch)
    ) {
      this.draftSelected.set([...this.displayOptions().map(option => String(option.key))]);
    } else if (!append && this.pendingSearchSelection) {
      const optionsSet = new Set(this.displayOptions().map(option => String(option.key)));
      this.draftSelected.set(this.pendingSearchSelection.filter(value => optionsSet.has(value)));
    }

    if (!append) {
      this.pendingSearchSelection = null;
    }

    if (!this.deferSelection()) {
      this.commitDraftSelection(false);
    }
  }

  private getLoaderSearchTermOnOpen(currentSearch: string): string {
    return currentSearch.trim();
  }

  private getSelectionForLoader(): string[] {
    return this.deferSelection() ? this.draftSelected() : this.selected();
  }

  private isSelectAllActive(): boolean {
    if (this.allSelected()) {
      return true;
    }
    return this.areVisibleOptionsSelected();
  }

  private areVisibleOptionsSelected(): boolean {
    return areZnSelectOptionsAllSelected(this.displayOptions(), this.draftSelected(), true);
  }

  private commitDraftSelection(emit: boolean = true): void {
    const nextValue = [...this.draftSelected()];
    this.selected.set(nextValue);
    this.currentValue.set(nextValue);

    if (emit) {
      if (this.allSelected() && !this.allSelectedSearchTerm()) {
        this.emitRawValue([]);
      } else {
        this.emitSelection(nextValue);
      }
    }
  }

  private syncSelectAllFlagFromDraftSelection(nextValue: string[]): void {
    const activeSearch = this.search().trim();
    if (activeSearch) {
      this.allSelected.set(false);
      return;
    }

    if (this.optionsLoader() && this.hasMore()) {
      this.allSelected.set(false);
      return;
    }

    const allSelected = areZnSelectOptionsAllSelected(this.displayOptions(), nextValue, true);
    this.allSelected.set(allSelected);

    if (allSelected) {
      this.allSelectedSearchTerm.set(null);
    }
  }

  private getOptionsContainerElement(): HTMLElement | undefined {
    return this.overlayRef?.overlayElement.querySelector<HTMLElement>('.zn-dropdown-menu-body') ?? undefined;
  }

  private loadOptions(params?: ZnSelectQueryParams, append: boolean = false): void {
    this.loader.loadOptions(params, append);
  }

  private focusTrigger(): void {
    this.selectTrigger?.nativeElement?.focus();
  }

  private focusSearchInput(): void {
    if (!this.showSearch()) {
      return;
    }

    requestAnimationFrame(() => {
      const input = this.overlayRef?.overlayElement.querySelector<HTMLInputElement>(
        '.filter-search input'
      );
      input?.focus();
    });
  }

  private scrollActiveOptionIntoView(smooth: boolean): void {
    const container = this.getOptionsContainerElement();
    if (!container) {
      return;
    }

    const items = container.querySelectorAll<HTMLElement>('.option-item');
    const index = this.activeIndex();
    if (index < 0 || index >= items.length) {
      return;
    }

    const item = items[index];
    requestAnimationFrame(() => {
      item.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: smooth ? 'smooth' : 'auto',
      });
    });
  }

  private selectAllForSearch(searchTerm: string): void {
    const loader = this.optionsLoader();
    if (!loader) {
      return;
    }

    this.selectAllSubscription?.unsubscribe();
    const requestId = ++this.selectAllRequestId;
    this.loading.set(true);
    this.loadingMore.set(false);

    this.selectAllSubscription = fetchAllZnSelectOptions(loader, this.pageSize, searchTerm)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: ({ items, total }) => {
          if (requestId !== this.selectAllRequestId) {
            return;
          }

          if (total !== null) {
            this.totalCount.set(total);
          }

          if (!items.length) {
            this.draftSelected.set([]);
            this.allSelected.set(false);
            this.allSelectedSearchTerm.set(null);
            return;
          }

          const labels = items.map(item => String(item.value));
          const ids = items.map(item => String(item.key));
          ids.forEach((id, index) => {
            const label = labels[index];
            this.optionsMap.set(id, label);
            this.selectedValueMap.set(id, label);
          });

          this.allSelected.set(true);
          this.allSelectedSearchTerm.set(searchTerm || null);
          this.draftSelected.set(ids);
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  private resolveSelectedLabels(value: string[] | string | null): void {
    if (!this.resolveIds()) {
      return;
    }

    const loader = this.optionsLoader();
    if (!loader || !value) {
      return;
    }

    const ids = Array.isArray(value) ? value : [value];
    ids.forEach(id => {
      const idStr = String(id);
      if (this.selectedValueMap.has(idStr) || this.pendingResolveIds.has(idStr)) {
        return;
      }

      const directOption = this.options().find(
        option => typeof option !== 'string' && String(option.key) === idStr
      ) as { key: string; value: string } | undefined;

      if (directOption) {
        const label = String(directOption.value);
        this.optionsMap.set(idStr, label);
        this.selectedValueMap.set(idStr, label);
        return;
      }

      this.pendingResolveIds.add(idStr);
      const subscription = loader({ ids: [idStr], forceRefresh: false, preferCache: true }).subscribe({
        next: response => {
          const payload = Array.isArray(response) ? response : response.items;
          this.pendingResolveIds.delete(idStr);
          if (!payload?.length) {
            return;
          }

          const match =
            payload.find(item => String(item?.key) === idStr) ?? (payload.length === 1 ? payload[0] : null);
          if (!match) {
            return;
          }

          const key = String(match.key);
          const label = String(match.value);
          this.optionsMap.set(key, label);
          this.selectedValueMap.set(key, label);
          this.syncFromValue(this.currentValue());
        },
        error: error => {
          this.toaster.error(this.buildLoadOptionsErrorMessage(error), null, { life: 5000 });
          this.pendingResolveIds.delete(idStr);
        },
      });

      this.labelLoadSubscriptions.push(subscription);
    });
  }

  private buildLoadOptionsErrorMessage(error?: unknown): string {
    return buildZnSelectLoadOptionsErrorMessage(
      this.localizationService,
      this.localizationResource(),
      this.errorEntityKey(),
      error
    );
  }
}
